import {
  doc,
  getDocs,
  collection,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { checkAndUpdateLevel } from "../utils/Utils";
import {
  buildCraftingAvailability,
  getCraftableCountFromAvailability,
  isFiniteCard,
  normalizeCardName,
  parseCraftRecipe,
} from "./craftRecipeUtils";
import { filterPackEligibleCharacterCards } from "./characterPackEligibility";

export const isMainProductionHost = () => {
  if (typeof window === "undefined") return false;
  const host = String(window.location?.hostname || "").toLowerCase();
  return host === "infinftroyale.com" || host === "www.infinftroyale.com";
};

export const isTestOnlyInfinityCard = (card) => {
  if (!card) return false;
  const raw = card.testOnly;
  if (typeof raw === "string") return raw.trim().toLowerCase() === "true";
  return !!raw;
};

export const getIssuableInfinityCards = (cards = []) => {
  const list = Array.isArray(cards) ? cards : [];
  if (!isMainProductionHost()) return list;
  return list.filter((card) => !isTestOnlyInfinityCard(card));
};

export function generateCardPack(
  infinityCards,
  characterCards,
  allInfinity,
  includeAtlVersions = false
) {
  const cCards = includeAtlVersions
    ? characterCards
    : [...characterCards].filter(
        (card) => !card?.version || Number(card?.version || 0) <= 1
      );
  const eligibleCharacterCards = filterPackEligibleCharacterCards(cCards);

  const issuableInfinityCards = getIssuableInfinityCards(infinityCards);
  const numCharacterCards = allInfinity
    ? 0
    : Math.min(getRandomInt(1, 2), eligibleCharacterCards.length);
  const numUniqueInfinityCards = getRandomInt(4, 7);

  const selectedCharacterCards = weightedSample(
    eligibleCharacterCards,
    numCharacterCards,
    getCharacterCardWeight
  );

  const selectedInfinityCards = weightedSample(
    issuableInfinityCards,
    numUniqueInfinityCards,
    getInfinityCardWeight
  );

  const finalPack = [...selectedCharacterCards];

  while (finalPack.length < 10) {
    const duplicatePool = selectedInfinityCards.length
      ? selectedInfinityCards
      : selectedCharacterCards;
    if (!duplicatePool.length) break;

    const randomCard =
      duplicatePool[Math.floor(Math.random() * duplicatePool.length)];
    finalPack.push({ ...randomCard });
  }

  const shuffledPack = shuffleArray(finalPack);

  const characterCount = shuffledPack.filter(
    (card) => card.type === "character"
  ).length;
  const infinityCount = shuffledPack.filter(
    (card) => card.type === "Infinity"
  ).length;

  console.log("Character Count:", characterCount);
  console.log("Infinity Count:", infinityCount);

  return shuffledPack.map((card) => ({
    ...card,
    _uuid: `${Date.now()}-${Math.random()}`,
  }));
}

function shuffleArray(array) {
  const arr = [...array];
  for (let index = arr.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [arr[index], arr[randomIndex]] = [arr[randomIndex], arr[index]];
  }
  return arr;
}

function weightedSample(cards, count, weightFn) {
  if (!cards || cards.length === 0) {
    console.warn("No cards provided to sample from.");
    return [];
  }

  const weighted = cards.map((card) => ({
    card,
    weight: weightFn(card),
  }));

  const sampled = [];
  let safety = 0;

  while (sampled.length < count && safety < 100) {
    const totalWeight = weighted.reduce((sum, item) => sum + item.weight, 0);
    let random = Math.random() * totalWeight;

    for (const { card, weight } of weighted) {
      random -= weight;
      if (random <= 0) {
        if (!sampled.includes(card) || cards.length <= count) {
          sampled.push(card);
        }
        break;
      }
    }

    safety += 1;
  }

  if (safety >= 100) {
    console.warn("weightedSample reached its safety limit.");
  }

  return sampled;
}

function getCharacterCardWeight(card) {
  const rarityWeights = {
    Common: 15,
    Uncommon: 10,
    Rare: 4,
    Epic: 2,
    Legendary: 1,
  };
  return rarityWeights[card.rarity] || 1;
}

function getInfinityCardWeight(card) {
  let baseWeight = card.finite ? 2 : 5;

  if (card.max !== undefined && card.max !== null) {
    const max = Math.max(1, card.max);
    baseWeight *= Math.min(10, Math.ceil(max / 10));
  }

  return baseWeight;
}

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const canCraftCard = (card, playerCards, decks = [], cardCatalog = []) => {
  return buildCraftingAvailability({
    card,
    playerCards,
    decks,
    cardCatalog,
  }).canCraft;
};

export const getCraftableCount = (
  card,
  playerCards,
  decks = [],
  cardCatalog = []
) => {
  const availability = buildCraftingAvailability({
    card,
    playerCards,
    decks,
    cardCatalog,
  });
  return getCraftableCountFromAvailability(availability);
};

export const isUsedToCraftFinite = (cardName, finiteCards = []) => {
  const normalizedName = normalizeCardName(cardName);
  return finiteCards.some((finiteCard) =>
    parseCraftRecipe(finiteCard).materials.some(
      (material) => normalizeCardName(material.name) === normalizedName
    )
  );
};

export const getCraftAmount = (card) => {
  const parsedRecipe = parseCraftRecipe(card);
  if (!parsedRecipe.valid) return 0;
  return parsedRecipe.materials.reduce(
    (total, material) => total + material.amount,
    0
  );
};

export const handleCraft = async (finiteCard, userData, options = {}) => {
  const playerCards = options.playerCards || userData?.cards || [];
  const decks = options.decks || userData?.decks || [];
  const cardCatalog = options.cardCatalog || [];
  const availability = buildCraftingAvailability({
    card: finiteCard,
    playerCards,
    decks,
    cardCatalog,
  });

  if (!userData?.id) {
    throw new Error("Missing user data for crafting.");
  }

  if (!isFiniteCard(finiteCard)) {
    throw new Error("Only finite cards can be crafted.");
  }

  if (!availability.valid) {
    throw new Error(availability.errors?.[0] || "This recipe is invalid.");
  }

  if (!availability.canCraft) {
    throw new Error("You do not have enough free materials to craft this card.");
  }

  const userCardsRef = collection(db, "users", userData.id, "Infinity cards");
  const userCardsSnapshot = await getDocs(userCardsRef);
  const docsByName = new Map();

  userCardsSnapshot.forEach((snapshot) => {
    docsByName.set(normalizeCardName(snapshot.data()?.name), snapshot);
  });

  const requiredByName = new Map();
  availability.materials.forEach((material) => {
    const key = normalizeCardName(material.name);
    const current = requiredByName.get(key) || {
      name: material.name,
      amount: 0,
    };
    current.amount += material.amount;
    requiredByName.set(key, current);
  });

  const craftedNameKey = normalizeCardName(finiteCard.name);
  const craftedExistingDoc = docsByName.get(craftedNameKey) || null;

  try {
    await runTransaction(db, async (transaction) => {
      const snapshotsByName = new Map();
      const namesToLoad = new Set([
        ...requiredByName.keys(),
        ...(craftedExistingDoc ? [craftedNameKey] : []),
      ]);

      for (const nameKey of namesToLoad) {
        const existingDoc = docsByName.get(nameKey);
        if (!existingDoc) continue;
        snapshotsByName.set(nameKey, await transaction.get(existingDoc.ref));
      }

      const nextAmountsByName = new Map();

      requiredByName.forEach((requiredEntry, nameKey) => {
        const materialSnapshot = snapshotsByName.get(nameKey);
        if (!materialSnapshot?.exists()) {
          throw new Error(`Missing material card: ${requiredEntry.name}.`);
        }

        const currentAmount = Number(materialSnapshot.data()?.amountOwned || 0);
        const materialView = availability.materials.find(
          (material) => normalizeCardName(material.name) === nameKey
        );
        const reservedCopies = Number(materialView?.reservedCopies || 0);
        const availableCopies = Math.max(0, currentAmount - reservedCopies);

        if (availableCopies < requiredEntry.amount) {
          throw new Error(
            `Not enough free copies of ${requiredEntry.name} to craft this card.`
          );
        }

        nextAmountsByName.set(nameKey, currentAmount - requiredEntry.amount);
      });

      if (craftedExistingDoc) {
        const craftedSnapshot = snapshotsByName.get(craftedNameKey);
        const craftedCurrentAmount = Number(
          craftedSnapshot?.data()?.amountOwned || 0
        );
        const currentAfterMaterials =
          nextAmountsByName.get(craftedNameKey) ?? craftedCurrentAmount;
        nextAmountsByName.set(craftedNameKey, currentAfterMaterials + 1);
      }

      requiredByName.forEach((requiredEntry, nameKey) => {
        const materialSnapshot = snapshotsByName.get(nameKey);
        transaction.update(materialSnapshot.ref, {
          amountOwned: nextAmountsByName.get(nameKey),
        });
      });

      if (craftedExistingDoc) {
        const craftedSnapshot = snapshotsByName.get(craftedNameKey);
        transaction.update(craftedSnapshot.ref, {
          amountOwned: nextAmountsByName.get(craftedNameKey),
        });
      } else {
        const newDocRef = doc(userCardsRef);
        transaction.set(newDocRef, {
          ...finiteCard,
          amountOwned: 1,
        });
      }
    });

    console.log(`Crafted 1x ${finiteCard.name}`);
    return {
      craftedCard: finiteCard,
    };
  } catch (error) {
    console.error("Crafting failed:", error);
    throw error;
  }
};

export const handleDestroy = async (card, count, userData) => {
  if (!userData?.id) {
    throw new Error("Missing user data for destroying a card.");
  }

  const destroyCount = Number(count);
  if (!Number.isInteger(destroyCount) || destroyCount <= 0) {
    throw new Error("Destroy count must be a positive whole number.");
  }

  const cardRef = doc(db, "users", userData.id, "Infinity cards", card.id);
  const userRef = doc(db, "users", userData.id);
  let updatedXp = 0;
  let nextAmount = 0;

  try {
    await runTransaction(db, async (transaction) => {
      const cardSnap = await transaction.get(cardRef);
      const userSnap = await transaction.get(userRef);

      if (!cardSnap.exists()) {
        throw new Error("You no longer own this card.");
      }

      const currentAmount = Number(cardSnap.data()?.amountOwned || 0);
      if (currentAmount < destroyCount) {
        throw new Error("You do not have enough copies to destroy.");
      }

      nextAmount = currentAmount - destroyCount;
      const currentXP = userSnap.exists() ? Number(userSnap.data()?.xp || 0) : 0;
      updatedXp = currentXP + destroyCount * 10;

      transaction.update(cardRef, {
        amountOwned: nextAmount,
      });

      transaction.update(userRef, {
        xp: updatedXp,
      });
    });

    await checkAndUpdateLevel(userData, updatedXp);

    console.log(`Destroyed ${destroyCount} ${card.name} for ${destroyCount * 10} XP`);
    return {
      nextAmount,
      updatedXp,
      xpGained: destroyCount * 10,
    };
  } catch (error) {
    console.error("Destroy failed:", error);
    throw error;
  }
};
